import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tbody,
  Tabs,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  BsArrowDown,
  BsArrowLeft,
  BsArrowUp,
  BsPencil,
  BsPlus,
  BsSearch,
  BsTrash,
} from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { ORGANIZATIONAL_STRUCTURE_TYPES } from "../services/organizationalStructureApi";
import useOrganizationalStructureManagement from "../hooks/useOrganizationalStructureManagement";

const DEFAULT_SORT = { column: "name", direction: "asc" };

const OrganizationalStructureMaintenance = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalMode, setModalMode] = useState("create");
  const [modalType, setModalType] = useState(
    ORGANIZATIONAL_STRUCTURE_TYPES.units,
  );
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const {
    units,
    departments,
    branches,
    categories,
    error,
    loading,
    saveItem,
    removeItem,
  } = useOrganizationalStructureManagement();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const singularLabelByType = useMemo(
    () => ({
      [ORGANIZATIONAL_STRUCTURE_TYPES.units]: "unit",
      [ORGANIZATIONAL_STRUCTURE_TYPES.departments]: "department",
      [ORGANIZATIONAL_STRUCTURE_TYPES.branches]: "branch",
      [ORGANIZATIONAL_STRUCTURE_TYPES.categories]: "category",
    }),
    [],
  );

  useEffect(() => {
    if (!error) {
      return;
    }

    toast({
      title: "Error fetching data",
      description:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  }, [error, toast]);

  const handleCreateClick = (type) => {
    setModalType(type);
    setModalMode("create");
    setEditingItem(null);
    setFormData({});
    onOpen();
  };

  const handleEditClick = (type, item) => {
    setModalType(type);
    setModalMode("edit");
    setEditingItem(item);
    setFormData({ ...item });
    onOpen();
  };

  const handleInputChange = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSaveItem = async () => {
    try {
      await saveItem(modalType, formData, editingItem);

      toast({
        title: `${singularLabelByType[modalType]} ${modalMode === "create" ? "created" : "updated"}`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      onClose();
    } catch (requestError) {
      toast({
        title: "Error saving item",
        description:
          requestError.response?.data?.error ||
          requestError.response?.data?.message ||
          requestError.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteItem = async (type, id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) {
      return;
    }

    try {
      await removeItem(type, id);

      toast({
        title: `${singularLabelByType[type]} deleted`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (requestError) {
      toast({
        title: "Error deleting item",
        description:
          requestError.response?.data?.error ||
          requestError.response?.data?.message ||
          requestError.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getFormFields = () => {
    switch (modalType) {
      case ORGANIZATIONAL_STRUCTURE_TYPES.units:
        return (
          <>
            <FormControl mb={4} isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name || ""}
                onChange={(event) =>
                  handleInputChange("name", event.target.value)
                }
                placeholder="e.g., Manufacturing"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Code</FormLabel>
              <Input
                value={formData.code || ""}
                onChange={(event) =>
                  handleInputChange("code", event.target.value.toUpperCase())
                }
                placeholder="e.g., MFG"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description || ""}
                onChange={(event) =>
                  handleInputChange("description", event.target.value)
                }
                placeholder="Unit description"
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel mb="0">Active</FormLabel>
              <Switch
                isChecked={formData.isActive !== false}
                onChange={(event) =>
                  handleInputChange("isActive", event.target.checked)
                }
              />
            </FormControl>
          </>
        );

      case ORGANIZATIONAL_STRUCTURE_TYPES.departments:
        return (
          <>
            <FormControl mb={4} isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name || ""}
                onChange={(event) =>
                  handleInputChange("name", event.target.value)
                }
                placeholder="e.g., Human Resources"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Code</FormLabel>
              <Input
                value={formData.code || ""}
                onChange={(event) =>
                  handleInputChange("code", event.target.value.toUpperCase())
                }
                placeholder="e.g., HR"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Parent Unit</FormLabel>
              <Input
                value={formData.parentUnit || ""}
                onChange={(event) =>
                  handleInputChange("parentUnit", event.target.value)
                }
                placeholder="Parent unit"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Cost Center</FormLabel>
              <Input
                value={formData.costCenter || ""}
                onChange={(event) =>
                  handleInputChange("costCenter", event.target.value)
                }
                placeholder="e.g., CC-001"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description || ""}
                onChange={(event) =>
                  handleInputChange("description", event.target.value)
                }
                placeholder="Department description"
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel mb="0">Active</FormLabel>
              <Switch
                isChecked={formData.isActive !== false}
                onChange={(event) =>
                  handleInputChange("isActive", event.target.checked)
                }
              />
            </FormControl>
          </>
        );

      case ORGANIZATIONAL_STRUCTURE_TYPES.branches:
        return (
          <>
            <FormControl mb={4} isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name || ""}
                onChange={(event) =>
                  handleInputChange("name", event.target.value)
                }
                placeholder="e.g., Mumbai HQ"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Code</FormLabel>
              <Input
                value={formData.code || ""}
                onChange={(event) =>
                  handleInputChange("code", event.target.value.toUpperCase())
                }
                placeholder="e.g., MUM"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>City</FormLabel>
              <Input
                value={formData.city || ""}
                onChange={(event) =>
                  handleInputChange("city", event.target.value)
                }
                placeholder="City"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>State</FormLabel>
              <Input
                value={formData.state || ""}
                onChange={(event) =>
                  handleInputChange("state", event.target.value)
                }
                placeholder="State"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Contact Email</FormLabel>
              <Input
                type="email"
                value={formData.contactEmail || ""}
                onChange={(event) =>
                  handleInputChange("contactEmail", event.target.value)
                }
                placeholder="branch@company.com"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Contact Phone</FormLabel>
              <Input
                value={formData.contactPhone || ""}
                onChange={(event) =>
                  handleInputChange("contactPhone", event.target.value)
                }
                placeholder="+91-22-1234-5678"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Capacity (headcount)</FormLabel>
              <Input
                type="number"
                value={formData.capacity || ""}
                onChange={(event) =>
                  handleInputChange("capacity", event.target.value)
                }
                placeholder="1000"
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={2}>
              <FormLabel mb="0">Is Headquarters</FormLabel>
              <Switch
                isChecked={formData.isHeadquarters || false}
                onChange={(event) =>
                  handleInputChange("isHeadquarters", event.target.checked)
                }
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel mb="0">Active</FormLabel>
              <Switch
                isChecked={formData.isActive !== false}
                onChange={(event) =>
                  handleInputChange("isActive", event.target.checked)
                }
              />
            </FormControl>
          </>
        );

      case ORGANIZATIONAL_STRUCTURE_TYPES.categories:
        return (
          <>
            <FormControl mb={4} isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name || ""}
                onChange={(event) =>
                  handleInputChange("name", event.target.value)
                }
                placeholder="e.g., IT Equipment"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Code</FormLabel>
              <Input
                value={formData.code || ""}
                onChange={(event) =>
                  handleInputChange("code", event.target.value.toUpperCase())
                }
                placeholder="e.g., IT"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Budget Limit</FormLabel>
              <Input
                type="number"
                value={formData.budgetLimit || ""}
                onChange={(event) =>
                  handleInputChange("budgetLimit", event.target.value)
                }
                placeholder="5000000"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description || ""}
                onChange={(event) =>
                  handleInputChange("description", event.target.value)
                }
                placeholder="Category description"
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel mb="0">Active</FormLabel>
              <Switch
                isChecked={formData.isActive !== false}
                onChange={(event) =>
                  handleInputChange("isActive", event.target.checked)
                }
              />
            </FormControl>
          </>
        );

      default:
        return null;
    }
  };

  const DataTable = ({ items, type }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const singularLabel = singularLabelByType[type];
    const tableHeaderBg = useColorModeValue("gray.50", "gray.700");

    const normalizedItems = useMemo(
      () =>
        (items || []).map((item) => ({
          ...item,
          statusLabel: item.isActive ? "Active" : "Inactive",
          searchableText: [
            item.name,
            item.code,
            item.description,
            item.parentUnit,
            item.costCenter,
            item.city,
            item.state,
            item.contactEmail,
            item.contactPhone,
            item.budgetLimit,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        })),
      [items],
    );

    const filteredItems = useMemo(
      () =>
        normalizedItems.filter((item) => {
          if (statusFilter === "active" && !item.isActive) {
            return false;
          }

          if (statusFilter === "inactive" && item.isActive) {
            return false;
          }

          if (!searchQuery.trim()) {
            return true;
          }

          return item.searchableText.includes(searchQuery.trim().toLowerCase());
        }),
      [normalizedItems, searchQuery, statusFilter],
    );

    const sortedItems = useMemo(() => {
      const sorted = [...filteredItems];

      sorted.sort((left, right) => {
        const leftValue =
          sortConfig.column === "status"
            ? left.statusLabel
            : (left[sortConfig.column] ?? "");
        const rightValue =
          sortConfig.column === "status"
            ? right.statusLabel
            : (right[sortConfig.column] ?? "");

        const comparison = String(leftValue).localeCompare(
          String(rightValue),
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          },
        );

        return sortConfig.direction === "asc" ? comparison : -comparison;
      });

      return sorted;
    }, [filteredItems, sortConfig]);

    const totalPages = Math.max(1, Math.ceil(sortedItems.length / pageSize));

    const paginatedItems = useMemo(() => {
      const startIndex = (page - 1) * pageSize;
      return sortedItems.slice(startIndex, startIndex + pageSize);
    }, [page, pageSize, sortedItems]);

    useEffect(() => {
      setPage(1);
    }, [pageSize, searchQuery, statusFilter, type]);

    useEffect(() => {
      if (page > totalPages) {
        setPage(totalPages);
      }
    }, [page, totalPages]);

    const handleSort = (column) => {
      setSortConfig((current) => {
        if (current.column === column) {
          return {
            column,
            direction: current.direction === "asc" ? "desc" : "asc",
          };
        }

        return { column, direction: "asc" };
      });
    };

    const renderSortIcon = (column) => {
      if (sortConfig.column !== column) {
        return null;
      }

      return sortConfig.direction === "asc" ? <BsArrowUp /> : <BsArrowDown />;
    };

    const clearTableControls = () => {
      setSearchQuery("");
      setStatusFilter("all");
      setSortConfig(DEFAULT_SORT);
      setPage(1);
      setPageSize(10);
    };

    const rangeStart = sortedItems.length === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeEnd = Math.min(page * pageSize, sortedItems.length);

    if (!items || items.length === 0) {
      return (
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={6}
          textAlign="center"
        >
          <Text color="gray.500">No {type} found</Text>
          <Button
            mt={4}
            colorScheme="blue"
            size="sm"
            leftIcon={<BsPlus />}
            onClick={() => handleCreateClick(type)}
          >
            Add {singularLabel}
          </Button>
        </Box>
      );
    }

    return (
      <Stack spacing={4}>
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <Stack spacing={4}>
            <Flex
              gap={3}
              direction={{ base: "column", lg: "row" }}
              align={{ base: "stretch", lg: "center" }}
              justify="space-between"
            >
              <InputGroup maxW={{ base: "full", lg: "360px" }}>
                <InputLeftElement pointerEvents="none">
                  <BsSearch color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder={`Search ${type}...`}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </InputGroup>

              <Stack
                direction={{ base: "column", md: "row" }}
                spacing={3}
                align={{ base: "stretch", md: "center" }}
              >
                <Select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  maxW={{ base: "full", md: "180px" }}
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active only</option>
                  <option value="inactive">Inactive only</option>
                </Select>

                <Select
                  value={pageSize}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                  maxW={{ base: "full", md: "150px" }}
                >
                  <option value={5}>5 / page</option>
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                </Select>

                <Button variant="outline" onClick={clearTableControls}>
                  Reset
                </Button>
              </Stack>
            </Flex>

            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "start", md: "center" }}
              gap={2}
            >
              <Text fontSize="sm" color="gray.500">
                Showing {rangeStart}-{rangeEnd} of {sortedItems.length} results
              </Text>
              <Text fontSize="sm" color="gray.500">
                Sorted by {sortConfig.column} ({sortConfig.direction})
              </Text>
            </Flex>
          </Stack>
        </Box>

        <Box
          overflowX="auto"
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
        >
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("name")}
                    rightIcon={renderSortIcon("name")}
                  >
                    Name
                  </Button>
                </Th>
                <Th>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("code")}
                    rightIcon={renderSortIcon("code")}
                  >
                    Code
                  </Button>
                </Th>
                <Th>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("status")}
                    rightIcon={renderSortIcon("status")}
                  >
                    Status
                  </Button>
                </Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedItems.length === 0 ? (
                <Tr>
                  <Td colSpan={4} textAlign="center" py={8}>
                    <Text color="gray.500">
                      No {type} match the current filters
                    </Text>
                  </Td>
                </Tr>
              ) : (
                paginatedItems.map((item) => (
                  <Tr key={item._id}>
                    <Td fontWeight="medium">{item.name}</Td>
                    <Td>{item.code || "-"}</Td>
                    <Td>
                      <Badge colorScheme={item.isActive ? "green" : "gray"}>
                        {item.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<BsPencil />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleEditClick(type, item)}
                          title="Edit"
                          aria-label={`Edit ${singularLabel}`}
                        />
                        <IconButton
                          icon={<BsTrash />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteItem(type, item._id)}
                          title="Delete"
                          aria-label={`Delete ${singularLabel}`}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        <Flex
          justify="space-between"
          align={{ base: "start", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={3}
        >
          <Text fontSize="sm" color="gray.500">
            Page {page} of {totalPages}
          </Text>

          <ButtonGroup size="sm" isAttached variant="outline">
            <Button onClick={() => setPage(1)} isDisabled={page === 1}>
              First
            </Button>
            <Button
              onClick={() => setPage((current) => current - 1)}
              isDisabled={page === 1}
            >
              Previous
            </Button>
            <Button
              onClick={() => setPage((current) => current + 1)}
              isDisabled={page >= totalPages}
            >
              Next
            </Button>
            <Button
              onClick={() => setPage(totalPages)}
              isDisabled={page >= totalPages}
            >
              Last
            </Button>
          </ButtonGroup>
        </Flex>
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box
        p={6}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="100vh"
      >
        <VStack>
          <Spinner size="lg" colorScheme="blue" />
          <Text>Loading organizational structure...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <IconButton
            icon={<BsArrowLeft />}
            variant="ghost"
            onClick={() => navigate(-1)}
            title="Go back"
            aria-label="Go back"
          />
          <Heading size="lg">Organizational Structure Maintenance</Heading>
        </HStack>
      </Flex>

      <Tabs index={activeTab} onChange={setActiveTab}>
        <TabList
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={2}
        >
          <Tab>Units ({units.length})</Tab>
          <Tab>Departments ({departments.length})</Tab>
          <Tab>Branches ({branches.length})</Tab>
          <Tab>Categories ({categories.length})</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <VStack spacing={4} align="stretch">
              <Button
                leftIcon={<BsPlus />}
                colorScheme="blue"
                onClick={() =>
                  handleCreateClick(ORGANIZATIONAL_STRUCTURE_TYPES.units)
                }
                w="fit-content"
              >
                Add Unit
              </Button>
              <DataTable
                items={units}
                type={ORGANIZATIONAL_STRUCTURE_TYPES.units}
              />
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch">
              <Button
                leftIcon={<BsPlus />}
                colorScheme="blue"
                onClick={() =>
                  handleCreateClick(ORGANIZATIONAL_STRUCTURE_TYPES.departments)
                }
                w="fit-content"
              >
                Add Department
              </Button>
              <DataTable
                items={departments}
                type={ORGANIZATIONAL_STRUCTURE_TYPES.departments}
              />
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch">
              <Button
                leftIcon={<BsPlus />}
                colorScheme="blue"
                onClick={() =>
                  handleCreateClick(ORGANIZATIONAL_STRUCTURE_TYPES.branches)
                }
                w="fit-content"
              >
                Add Branch
              </Button>
              <DataTable
                items={branches}
                type={ORGANIZATIONAL_STRUCTURE_TYPES.branches}
              />
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch">
              <Button
                leftIcon={<BsPlus />}
                colorScheme="blue"
                onClick={() =>
                  handleCreateClick(ORGANIZATIONAL_STRUCTURE_TYPES.categories)
                }
                w="fit-content"
              >
                Add Category
              </Button>
              <DataTable
                items={categories}
                type={ORGANIZATIONAL_STRUCTURE_TYPES.categories}
              />
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {modalMode === "create" ? "Create" : "Edit"}{" "}
            {singularLabelByType[modalType]}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>{getFormFields()}</ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleSaveItem}>
                Save
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default OrganizationalStructureMaintenance;
